import { useCallback, useEffect, useMemo, useRef } from 'react';

export interface ITimeoutHandle {
  /** Run `callback` after `delayMs`, cancelling whatever this handle had pending first. */
  schedule: (callback: () => void, delayMs: number) => void;
  /** Cancel the pending callback, if there is one. */
  cancel: () => void;
}

/**
 * One timer a component owns: re-arming replaces it, and unmounting cancels it.
 *
 * ## ⛔ A timer that outlives its component is not harmless
 *
 * Seven settings screens armed a 2.2-second `setTimeout` to clear a "Saved" flash and dropped the
 * handle (I-0360). A reader who navigates away is then written to after their screen is gone, and
 * in the test environment the late `setState` fired after jsdom was torn down — failing a whole
 * vitest run from outside any test, with every test green. The same four lines had been copied by
 * hand into the screens that got it right, which is how the others came to get it wrong; this is
 * the one copy.
 *
 * Re-arming cancels first so an older callback cannot land on a newer state — a second save within
 * the window would otherwise have its flash cleared early by the first save's timer.
 */
export function useTimeout(): ITimeoutHandle {
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const cancel = useCallback(() => {
    if (timer.current === undefined) return;
    clearTimeout(timer.current);
    timer.current = undefined;
  }, []);

  const schedule = useCallback(
    (callback: () => void, delayMs: number) => {
      cancel();
      timer.current = setTimeout(() => {
        timer.current = undefined;
        callback();
      }, delayMs);
    },
    [cancel],
  );

  useEffect(() => cancel, [cancel]);

  return useMemo(() => ({ schedule, cancel }), [schedule, cancel]);
}
